import { Suspense, lazy, useEffect, useState } from "react";

import { Agenda } from "@/features/agenda/Agenda";
import { ArquivoDeOportunidades } from "@/features/arquivo/ArquivoDeOportunidades";
import { Onboarding } from "@/features/onboarding/Onboarding";
import { marcarOnboardingConcluido, onboardingConcluido } from "@/features/onboarding/estado";
import { QuadroDeOportunidades } from "@/features/board/QuadroDeOportunidades";
import { pedirCriacao } from "@/features/board/atalhoDeCriacao";
import { EVENTO_DE_ABERTURA_DO_QUADRO, projetoPedido } from "@/features/entregas/navegacao";
import { TelaDeCadastro } from "@/features/auth/TelaDeCadastro";
import { TelaDeEntrada } from "@/features/auth/TelaDeEntrada";
import { useSessao } from "@/features/auth/SessaoProvider";
import { AceitarConvite } from "@/features/convites/AceitarConvite";
import { EsqueciMinhaSenha } from "@/features/senha/EsqueciMinhaSenha";
import { NovaSenha } from "@/features/senha/NovaSenha";
import { ConvidarPessoa } from "@/features/convites/ConvidarPessoa";
import { AvisoDeVerificacao } from "@/features/verificacao/AvisoDeVerificacao";
import { ConfirmarEmail } from "@/features/verificacao/ConfirmarEmail";
import { DesafioDoSegundoFator } from "@/features/mfa/DesafioDoSegundoFator";
import { deveMostrar, useGuias } from "@/features/guias/useGuias";
import { TELAS_COM_GUIA } from "@/features/guias/telas";
import { Botao } from "@/shared/ui/Botao";
import { LimiteDeErro } from "@/shared/ui/LimiteDeErro";
import { MaisDoCelular } from "@/shared/ui/MaisDoCelular";
import { NavegacaoInferior } from "@/shared/ui/NavegacaoInferior";
import { Topbar } from "@/shared/ui/Topbar";
import { destinosDoCelular } from "./destinosDoCelular";
import { itensDoMais } from "./itensDoMais";
import { rotaDeEntregas, rotaPublica } from "./rotas";

/** Sob demanda, e a medição é o motivo (ADR-0019).
 *
 * A conta traz junto o segundo fator, que arrasta o `qrcode.react`: no bundle
 * de entrada isso custava ~2,7 kB gzip de um orçamento que tinha menos de 1 kB
 * de folga. Quem entra para trabalhar no funil não paga por uma tela visitada
 * uma vez na vida da conta.
 *
 * O desafio do login **continua estático**: ele está no caminho de quem entra
 * todo dia, e uma fatia extra ali seria um espera a mais entre a senha e o
 * código.
 */
const MinhaConta = lazy(async () => ({
  default: (await import("@/features/conta/MinhaConta")).MinhaConta,
}));

/** Também sob demanda: só administradores abrem, e o orçamento de entrada tem
 * menos de 2 kB de folga (ver `.size-limit.md`). */
const Membros = lazy(async () => ({
  default: (await import("@/features/membros/Membros")).Membros,
}));

/** Também sob demanda: quem administra o vocabulário de etiquetas entra aqui
 * uma vez por mês, e não no caminho de quem trabalha no funil. */
const Etiquetas = lazy(async () => ({
  default: (await import("@/features/etiquetas/Etiquetas")).Etiquetas,
}));

/** Também sob demanda: o painel só é aberto quando alguém quer olhar o mês,
 * e nunca no caminho de quem entra para trabalhar no funil. */
const Painel = lazy(async () => ({
  default: (await import("@/features/dashboard/Painel")).Painel,
}));

/** Também sob demanda: quem exporta a trilha é o papel `auditor`, e a maioria
 * das sessões nunca abre esta tela. */
const RelatoriosDeAuditoria = lazy(async () => ({
  default: (await import("@/features/auditoria/RelatoriosDeAuditoria")).RelatoriosDeAuditoria,
}));

/** Também sob demanda, e esta é a mais rara de todas: o canal de WhatsApp é
 * configurado **uma vez** na vida da conta, por um administrador. */
const Canal = lazy(async () => ({
  default: (await import("@/features/canal/Canal")).Canal,
}));

/** Também sob demanda: o quadro de entregas só abre quando alguém cria ou
 * segue um projeto (Tarefas 13 e 14) — nunca no caminho de quem entra para
 * trabalhar no funil. */
const QuadroDeEntregas = lazy(async () => ({
  default: (await import("@/features/entregas/QuadroDeEntregas")).QuadroDeEntregas,
}));

/** O guia e a ajuda vivem na mesma fatia, carregada quando um dos dois
 * aparece. Juntos com o texto das oito telas eles custavam ~4,5 kB no primeiro
 * carregamento, e o orçamento tem menos de 6 kB de folga. */
const GuiaDaTela = lazy(async () => ({
  default: (await import("@/features/guias/GuiaDaTela")).GuiaDaTela,
}));

const AjudaDaTela = lazy(async () => ({
  default: (await import("@/features/guias/AjudaDaTela")).AjudaDaTela,
}));

/** Composição da aplicação.
 *
 * Continua sem biblioteca de rotas, e agora há **três** endereços que importam:
 * o link do convite, o de nova senha e o de confirmação de e-mail. Os três são
 * públicos, chegam por fora e não têm navegação entre si — reconhecê-los pelo
 * caminho da URL resolve os três casos em uma função.
 *
 * A árvore de rotas do TanStack passa a valer quando houver navegação entre
 * telas autenticadas, com link de uma para a outra. Não é o caso ainda.
 */
export function App() {
  const { estado, segundoFatorPendente, concluirSegundoFator, desistirDoSegundoFator } =
    useSessao();
  const [querCadastro, setQuerCadastro] = useState(false);
  const [querRecuperar, setQuerRecuperar] = useState(false);
  const rota = rotaPublica(window.location.pathname);

  // Antes de qualquer decisão sobre sessão: quem chega por um destes links pode
  // já estar logado em outra conta, e o link precisa continuar valendo.
  if (rota?.tipo === "convite") {
    return (
      <AceitarConvite
        token={rota.token}
        aoEntrar={() => {
          // Navegação de verdade, e não troca de estado: o aceite já deixou o
          // cookie de refresh, e a montagem limpa faz a renovação silenciosa
          // sozinha. Também tira `/convite/...` da barra — um F5 ali tentaria
          // aceitar um convite já consumido.
          window.location.assign("/");
        }}
      />
    );
  }

  if (rota?.tipo === "nova-senha") {
    return (
      <NovaSenha
        token={rota.token}
        aoConcluir={() => {
          // A troca revogou as sessões, então não há o que renovar: a raiz
          // mostra o login, que é onde a pessoa precisa estar.
          window.location.assign("/");
        }}
      />
    );
  }

  if (rota?.tipo === "verificar-email") {
    return (
      <ConfirmarEmail
        token={rota.token}
        aoSeguir={() => {
          // Navegação de verdade: a montagem limpa refaz a sessão e o aviso de
          // "confirme seu e-mail" some sozinho, porque agora está confirmado.
          window.location.assign("/");
        }}
      />
    );
  }

  if (estado.situacao === "verificando") return <Verificando />;

  // Antes de decidir entre login e cadastro: a senha já foi aceita, e só falta
  // o código. Cair no formulário de e-mail aqui apagaria o token de etapa e
  // faria a pessoa digitar a senha de novo.
  if (segundoFatorPendente) {
    return (
      <DesafioDoSegundoFator
        aoConfirmar={concluirSegundoFator}
        aoCancelar={desistirDoSegundoFator}
      />
    );
  }

  if (estado.situacao === "anonimo") {
    if (querRecuperar) {
      return (
        <EsqueciMinhaSenha
          aoVoltar={() => {
            setQuerRecuperar(false);
          }}
        />
      );
    }
    return querCadastro ? (
      <TelaDeCadastro
        aoVoltar={() => {
          setQuerCadastro(false);
        }}
      />
    ) : (
      <TelaDeEntrada
        aoPedirCadastro={() => {
          setQuerCadastro(true);
        }}
        aoEsquecerSenha={() => {
          setQuerRecuperar(true);
        }}
      />
    );
  }

  return <AreaLogada />;
}

/** Enquanto a renovação silenciosa acontece. Sem este estado, a tela piscaria
 * o login antes de reconhecer uma sessão que já era válida. */
function Verificando() {
  return (
    <div className="grid min-h-dvh place-items-center bg-canvas">
      <p role="status" className="font-texto text-body text-texto-suave">
        Verificando sua sessão…
      </p>
    </div>
  );
}

/** As telas que abrem **sobre** o conteúdo, sem trocar a página inteira.
 *
 * **Estes nomes são os nomes das telas**, e não apelidos internos: é por eles
 * que o guia e a ajuda encontram o conteúdo (`telaVisivel`), e é por eles que o
 * servidor grava quem já viu o quê. A auditoria chamava-se `"trilha"` aqui e
 * `"auditoria"` em todo o resto — o guia dela nunca apareceu e o `?` respondia
 * que a tela não tinha ajuda escrita, sem erro em lugar nenhum. */
type Folha = "conta" | "auditoria" | "etiquetas" | "indicadores" | "pessoas" | "canal" | "mais";

function AreaLogada() {
  const { estado, temPermissao, sair } = useSessao();
  const [convidando, setConvidando] = useState(false);
  /** Qual folha está aberta sobre o conteúdo, se alguma.
   *
   * **Uma só, e não seis sinalizadores.** Com a barra visível em toda tela, a
   * pessoa alcança "Pessoas" estando em "Etiquetas" — e com um sinalizador por
   * folha as duas ficavam abertas ao mesmo tempo, empilhadas. Um estado único
   * torna a regra impossível de quebrar: abrir uma fecha a outra. */
  const [folha, setFolha] = useState<Folha | null>(null);
  /** O painel de ajuda (`?`) está aberto. */
  const [ajudaAberta, setAjudaAberta] = useState(false);
  /** Guia pedido de novo pela ajuda, mesmo já tendo sido visto. */
  const [guiaForcado, setGuiaForcado] = useState<string | null>(null);
  const guias = useGuias();
  /** Qual tela inteira está no lugar do funil. O arquivo e a agenda trocam a
   * página, e não abrem folha: quem vai planejar o dia ou procurar o que
   * perdeu no trimestre não está no meio de outra tarefa.
   *
   * `entregas` é a primeira tela com endereço de verdade
   * (`/entregas/:projetoId`, `rotas.ts`): quem chega pelo card do CRM ou por
   * um link precisa achar o quadro mesmo depois de um F5, o que "funil" |
   * "arquivo" | "agenda" nunca precisaram — elas não têm link próprio. */
  const [tela, setTela] = useState<
    "funil" | "arquivo" | "agenda" | { tipo: "entregas"; projetoId: string }
  >(() => {
    const projetoId = rotaDeEntregas(window.location.pathname);
    return projetoId === null ? "funil" : { tipo: "entregas", projetoId };
  });
  // O guia é para quem **acabou de criar a conta**, não para todo login: um
  // usuário de meses abrindo o produto em outro navegador não pode receber
  // "registre a primeira oportunidade" tendo cinquenta. O `localStorage`
  // complementa — quem já concluiu não revê nem cadastrando de novo no mesmo
  // navegador por engano.
  const tenantId = estado.situacao === "autenticado" ? estado.tenant.id : "";
  const recemCadastrado = estado.situacao === "autenticado" && estado.recemCadastrado === true;
  const [guiaDispensado, setGuiaDispensado] = useState(() => onboardingConcluido(tenantId));
  /** Trocar de tela fecha a folha aberta — são dois planos, e o de baixo é o
   * que a barra comanda. */
  function irPara(destino: "funil" | "arquivo" | "agenda") {
    // Saindo de `/entregas/:id` para uma tela sem endereço próprio: sem isto
    // um F5 depois de "Voltar" reabriria o mesmo quadro, porque a barra ainda
    // diria `/entregas/algo`.
    if (window.location.pathname.startsWith("/entregas/")) {
      window.history.pushState({}, "", "/");
    }
    setTela(destino);
    setFolha(null);
  }
  function voltarAoFunil() {
    irPara("funil");
  }
  /** Fechar a folha devolve o conteúdo que estava atrás dela — que pode ser a
   * agenda ou o arquivo, e não necessariamente o funil. */
  function fecharFolha() {
    setFolha(null);
  }

  // A ponte com "abrir projeto pelo card do CRM" (Tarefa 14,
  // `features/entregas/navegacao.ts`): quem cria o projeto está no painel da
  // oportunidade, e é aqui — não lá — que se decide qual tela aparece.
  useEffect(() => {
    function aoPedirem(evento: Event) {
      const projetoId = projetoPedido(evento);
      if (projetoId === null) return;
      window.history.pushState({}, "", `/entregas/${projetoId}`);
      setTela({ tipo: "entregas", projetoId });
      setFolha(null);
    }
    window.addEventListener(EVENTO_DE_ABERTURA_DO_QUADRO, aoPedirem);
    return () => {
      window.removeEventListener(EVENTO_DE_ABERTURA_DO_QUADRO, aoPedirem);
    };
  }, []);

  // Qual tela está à frente, do ponto de vista de quem olha: a folha aberta
  // manda, porque é ela que ocupa a tela. É esse nome que decide qual guia
  // aparece e qual ajuda o `?` abre. `entregas` ainda não tem guia escrito
  // (`TELAS_COM_GUIA`) — `deveMostrar` simplesmente não encontra passos para
  // ela e não mostra nada, sem precisar de um caso especial aqui.
  const telaVisivel = folha ?? (typeof tela === "string" ? tela : "entregas");
  // Só para a navegação inferior do celular, que não conhece `entregas`: ela
  // não tem destino próprio ali. "Funil" fica marcado por padrão — a mesma
  // aproximação que a barra superior já fazia para toda folha antes desta
  // tela existir, e não uma regressão nova.
  const telaDeConteudo: "funil" | "arquivo" | "agenda" = typeof tela === "string" ? tela : "funil";
  if (estado.situacao !== "autenticado") return null;

  if (recemCadastrado && !guiaDispensado) {
    return (
      <Onboarding
        nomeDaEmpresa={estado.tenant.name}
        aoConcluir={() => {
          marcarOnboardingConcluido(estado.tenant.id);
          setGuiaDispensado(true);
        }}
        // Mesmo portão do botão da barra e do servidor: o número é da empresa
        // inteira, e quem não administra a conta tomaria `403` na tela do canal.
        podeConectarWhatsApp={temPermissao("whatsapp:manage")}
        // O guia leva até a tela do canal em vez de repetir a configuração
        // dela: o passo termina onde o trabalho continua, e não numa segunda
        // cópia do roteiro da Meta.
        aoConectarWhatsApp={() => {
          marcarOnboardingConcluido(estado.tenant.id);
          setGuiaDispensado(true);
          setFolha("canal");
        }}
      />
    );
  }

  return (
    // `pb-20 sm:pb-0`: a navegação inferior é `fixed`, e sem esta folga o
    // último item de qualquer tela nasce debaixo dela — o "Sair" do menu, o
    // último card de uma coluna, o botão de uma folha. A folga vale para a área
    // logada inteira, e não tela a tela, porque a barra também vale.
    <div className="min-h-dvh bg-canvas pb-20 sm:pb-0">
      {/* Acima da tela e fora do `switch` dela: o aviso vale para a área logada
          inteira. Numa posição estável do JSX ele sobrevive à troca de tela —
          dentro de cada ramo, quem o dispensasse no funil o veria de volta ao
          voltar da agenda, e um aviso que reaparece sozinho é ruído.

          `email_verified` é `UserPayload` (`components["schemas"]`), sempre
          presente com default — não o campo opcional que o tipo escrito à
          mão fingia ser pensando num `/auth/me` que `estado.usuario` nunca
          usa. */}
      {!estado.usuario.email_verified && <AvisoDeVerificacao email={estado.usuario.email} />}

      {/* A barra vive **aqui**, e não dentro da tela do funil.

          Ela morava lá dentro, e por isso sumia: agenda, arquivo e cada folha
          — etiquetas, auditoria, pessoas, canal, indicadores — devolviam o
          próprio conteúdo no lugar do funil, levando a navegação junto. Quem
          entrava em Etiquetas ficava com um "Voltar ao funil" como única saída,
          e para ir a Pessoas tinha de passar pelo funil no meio.

          Fora do `switch`, ela é o teto da área logada: existe em toda tela e
          acompanha a rolagem (`sticky`). */}
      <BarraDoProduto
        empresa={estado.tenant.name}
        usuario={estado.usuario.full_name}
        mfaLigado={estado.usuario.mfa_enabled}
        temPermissao={temPermissao}
        aoIrPara={irPara}
        aoAbrir={setFolha}
        aoConvidar={() => {
          setFolha(null);
          setConvidando(true);
        }}
        aoPedirAjuda={() => {
          setAjudaAberta(true);
        }}
        aoSair={() => void sair()}
      />

      {/* A navegação do polegar (DESIGN.md § 2.2), só abaixo de 640px.
          A barra superior quebrava em três linhas no celular e, somada ao aviso
          de verificação, comia um terço da tela antes do primeiro card. */}
      <NavegacaoInferior
        aoCriar={() => {
          setFolha(null);
          irPara("funil");
          // Num quadro que acabou de montar, o ouvinte ainda não existe quando
          // o clique acontece — o `setTimeout(0)` põe o pedido depois da
          // renderização, e não antes dela.
          setTimeout(pedirCriacao, 0);
        }}
        destinos={destinosDoCelular(telaDeConteudo, folha !== null, {
          aoIrPara: irPara,
          aoAbrirMais: () => {
            setFolha("mais");
          },
        })}
      />

      {/* O guia e a ajuda ficam **fora** do `switch` de tela, pelo mesmo motivo
          da barra: os dois valem para qualquer tela, e quem decide qual
          conteúdo mostrar é o nome da tela visível. */}
      {/* Montado só quando há guia a mostrar, e não sempre: é isso que mantém
          o texto dos oito guias fora do primeiro carregamento. Sem `fallback` —
          um "carregando" para algo que a pessoa não pediu seria pior que a
          fração de segundo a mais. */}
      {(guiaForcado === telaVisivel ||
        deveMostrar(guias.data, telaVisivel, TELAS_COM_GUIA.has(telaVisivel))) && (
        <Suspense fallback={null}>
          <GuiaDaTela
            tela={telaVisivel}
            forcado={guiaForcado === telaVisivel}
            aoTerminarForcado={() => {
              setGuiaForcado(null);
            }}
          />
        </Suspense>
      )}

      {ajudaAberta && (
        <Suspense fallback={null}>
          <AjudaDaTela
            tela={telaVisivel}
            aoFechar={() => {
              setAjudaAberta(false);
            }}
            aoRever={() => {
              setGuiaForcado(telaVisivel);
            }}
          />
        </Suspense>
      )}

      {folha === "mais" && (
        <MaisDoCelular
          aoVoltar={fecharFolha}
          itens={itensDoMais(temPermissao, {
            aoAbrirIndicadores: () => {
              setFolha("indicadores");
            },
            aoAbrirPessoas: () => {
              setFolha("pessoas");
            },
            aoConvidar: () => {
              setFolha(null);
              setConvidando(true);
            },
            aoAbrirEtiquetas: () => {
              setFolha("etiquetas");
            },
            aoAbrirCanal: () => {
              setFolha("canal");
            },
            aoAbrirAuditoria: () => {
              setFolha("auditoria");
            },
            aoAbrirConta: () => {
              setFolha("conta");
            },
            aoPedirAjuda: () => {
              setFolha(null);
              setAjudaAberta(true);
            },
            aoSair: () => void sair(),
          })}
        />
      )}

      {folha === "conta" && (
        <Suspense fallback={<Abrindo />}>
          <MinhaConta aoVoltar={fecharFolha} />
        </Suspense>
      )}

      {folha === "auditoria" && (
        <Suspense fallback={<Abrindo />}>
          <RelatoriosDeAuditoria aoVoltar={fecharFolha} />
        </Suspense>
      )}

      {folha === "etiquetas" && (
        <Suspense fallback={<Abrindo />}>
          <Etiquetas aoVoltar={fecharFolha} />
        </Suspense>
      )}

      {folha === "indicadores" && (
        <Suspense fallback={<Abrindo />}>
          <Painel aoVoltar={fecharFolha} />
        </Suspense>
      )}

      {folha === "pessoas" && (
        <Suspense fallback={<Abrindo />}>
          <Membros
            aoVoltar={fecharFolha}
            // A folha de Pessoas **fica aberta** por baixo, ao contrário do
            // que faz o botão da barra: quem convida a partir daqui quer ver a
            // pessoa entrar na lista, e fechá-la exigiria reabrir para
            // conferir o resultado.
            aoConvidar={() => {
              setConvidando(true);
            }}
          />
        </Suspense>
      )}

      {folha === "canal" && (
        <Suspense fallback={<Abrindo />}>
          <Canal aoVoltar={fecharFolha} />
        </Suspense>
      )}

      {/* Escondido em vez de desmontado: as folhas acima são modais, e
          desmontar o funil atrás delas descartaria o filtro digitado e as
          páginas já carregadas de cada coluna. */}
      <div hidden={folha !== null}>
        <Telas
          tela={tela}
          voltarAoFunil={voltarAoFunil}
          convidando={convidando}
          setConvidando={setConvidando}
        />
      </div>
    </div>
  );
}

/** O mesmo recado enquanto qualquer folha carrega o próprio pedaço de código. */
function Abrindo() {
  return (
    <p role="status" className="p-6 font-texto text-body text-texto-suave">
      Abrindo…
    </p>
  );
}

interface TelasProps {
  tela: "funil" | "arquivo" | "agenda" | { tipo: "entregas"; projetoId: string };
  voltarAoFunil: () => void;
  convidando: boolean;
  setConvidando: (convidando: boolean) => void;
}

function Telas({ tela, voltarAoFunil, convidando, setConvidando }: TelasProps) {
  const { estado } = useSessao();
  if (estado.situacao !== "autenticado") return null;

  if (typeof tela === "object") {
    return (
      <LimiteDeErro
        area="O quadro de entregas"
        acaoSecundaria={{ rotulo: "Voltar ao funil", aoAcionar: voltarAoFunil }}
      >
        <main className="mx-auto flex max-w-[1600px] flex-col gap-6 px-6 py-8">
          <Suspense fallback={<Abrindo />}>
            <QuadroDeEntregas projetoId={tela.projetoId} aoVoltar={voltarAoFunil} />
          </Suspense>
        </main>
      </LimiteDeErro>
    );
  }

  if (tela === "arquivo") {
    return (
      <LimiteDeErro
        area="O arquivo de fechadas"
        acaoSecundaria={{ rotulo: "Voltar ao funil", aoAcionar: voltarAoFunil }}
      >
        <ArquivoDeOportunidades aoVoltar={voltarAoFunil} />
      </LimiteDeErro>
    );
  }

  if (tela === "agenda") {
    return (
      <LimiteDeErro
        area="A agenda"
        acaoSecundaria={{ rotulo: "Voltar ao funil", aoAcionar: voltarAoFunil }}
      >
        <Agenda aoVoltar={voltarAoFunil} />
      </LimiteDeErro>
    );
  }

  return (
    <main className="mx-auto flex max-w-[1600px] flex-col gap-6 px-6 py-8">
      {convidando && (
        <LimiteDeErro
          area="O convite"
          acaoSecundaria={{
            rotulo: "Fechar",
            aoAcionar: () => {
              setConvidando(false);
            },
          }}
        >
          <ConvidarPessoa
            aoFechar={() => {
              setConvidando(false);
            }}
          />
        </LimiteDeErro>
      )}

      {/* Cada região com o seu limite, e não um só na raiz: aqui a barra fica
          de pé quando o funil cai, e é ela que oferece o arquivo, o convite e a
          saída. Um limite único no topo devolveria a mesma tela em branco de
          antes, só que com moldura. */}
      <LimiteDeErro area="O funil">
        <QuadroDeOportunidades />
      </LimiteDeErro>
    </main>
  );
}

/** A navegação do produto. Uma só, para todas as telas.
 *
 * Recebe o que fazer, e não o estado de quem abre o quê: quem decide se
 * "Pessoas" troca a tela ou abre uma folha é a área logada, e a barra só diz
 * que a pessoa clicou.
 */
function BarraDoProduto({
  empresa,
  usuario,
  mfaLigado,
  temPermissao,
  aoIrPara,
  aoAbrir,
  aoConvidar,
  aoPedirAjuda,
  aoSair,
}: {
  empresa: string;
  usuario: string;
  mfaLigado: boolean;
  temPermissao: (codigo: string) => boolean;
  aoIrPara: (tela: "funil" | "arquivo" | "agenda") => void;
  aoAbrir: (folha: Folha) => void;
  aoConvidar: () => void;
  aoPedirAjuda: () => void;
  aoSair: () => void;
}) {
  return (
    // Faixa de largura inteira, e o conteúdo é que fica contido: uma barra que
    // respeitasse a mesma margem do board pareceria mais um cartão da tela em
    // vez do teto dela.
    <Topbar
      empresa={empresa}
      contexto="CRM · Pipeline comercial"
      usuario={usuario}
      // O próprio nome abre a conta, e o ponto âmbar do segundo fator veio
      // junto: a barra já tinha dez ações, e "Segurança" era um item a mais
      // para uma coisa que a pessoa procura pelo nome dela.
      aoAbrirConta={() => {
        aoAbrir("conta");
      }}
      alerta={!mfaLigado}
      acoes={
        <>
          {/* O nome do produto leva ao funil, como em qualquer lugar da web —
              e, agora que a barra existe em toda tela, é o caminho mais curto
              de volta ao trabalho. */}
          <Botao
            tamanho="sm"
            variante="fantasma"
            onClick={() => {
              aoIrPara("funil");
            }}
          >
            Funil
          </Botao>
          {/* Agenda antes de Fechadas: uma responde "o que faço hoje", a
              outra "o que aconteceu". A primeira é diária. */}
          <Botao
            tamanho="sm"
            variante="fantasma"
            onClick={() => {
              aoIrPara("agenda");
            }}
          >
            Agenda
          </Botao>
          <Botao
            tamanho="sm"
            variante="fantasma"
            onClick={() => {
              aoIrPara("arquivo");
            }}
          >
            Fechadas
          </Botao>
          {temPermissao("user:invite") && (
            // Fantasma como os vizinhos. Era o único `secundario` no meio de
            // dez fantasmas: numa barra em que nada marca a tela ativa, a
            // pílula branca lia-se como "você está aqui" — apontando para a
            // ação mais rara do produto. O lugar dela é a tela Pessoas, que
            // agora a tem.
            <Botao tamanho="sm" variante="fantasma" onClick={aoConvidar}>
              Convidar pessoa
            </Botao>
          )}
          {/* Só para quem administra. O botão aparecer e a rota responder
              `403` seria pior que não aparecer: a pessoa aprende que o
              produto quebra em vez de que aquilo não é dela. */}
          {temPermissao("user:manage") && (
            <Botao
              tamanho="sm"
              variante="fantasma"
              onClick={() => {
                aoAbrir("pessoas");
              }}
            >
              Pessoas
            </Botao>
          )}
          {/* Sem portão de permissão: o painel só soma o que o funil já
              mostra, e quem entra no funil vê o funil inteiro (SPEC-ANL-001
              § 5). Exigir permissão aqui daria dois donos à mesma
              informação. */}
          <Botao
            tamanho="sm"
            variante="fantasma"
            onClick={() => {
              aoAbrir("indicadores");
            }}
          >
            Indicadores
          </Botao>
          {/* Mesmo portão do servidor: quem organiza o funil decide quais
              etiquetas existem, e quem vende só marca (SPEC-OPP-050 § 1). */}
          {temPermissao("opportunity:manage") && (
            <Botao
              tamanho="sm"
              variante="fantasma"
              onClick={() => {
                aoAbrir("etiquetas");
              }}
            >
              Etiquetas
            </Botao>
          )}
          {/* Mesmo portão do servidor. Trocar este número muda para onde vai
              a conversa da empresa inteira — é decisão de quem administra a
              conta, não de quem usa o funil. */}
          {temPermissao("whatsapp:manage") && (
            <Botao
              tamanho="sm"
              variante="fantasma"
              onClick={() => {
                aoAbrir("canal");
              }}
            >
              WhatsApp
            </Botao>
          )}
          {/* `audit:export` e não `audit:read`: quem só lê a trilha na tela
              não tem como exportá-la, e um botão que leva a `403` ensina que
              o produto quebra em vez de que aquilo não é dele. */}
          {temPermissao("audit:export") && (
            <Botao
              tamanho="sm"
              variante="fantasma"
              onClick={() => {
                aoAbrir("auditoria");
              }}
            >
              Auditoria
            </Botao>
          )}
          {/* O `?` fica **antes** de "Sair", e não no fim: quem procura ajuda
              não pode encontrar a saída primeiro. */}
          <Botao tamanho="sm" variante="fantasma" onClick={aoPedirAjuda} aria-label="Ajuda">
            ?
          </Botao>
          <Botao tamanho="sm" variante="fantasma" onClick={aoSair}>
            Sair
          </Botao>
        </>
      }
    />
  );
}
